import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../constants/Colors';
import Style from '../../../../../constants/Style';
import PressableStyled from '../../../../../components/PressableStyled';

interface TargetCardProps {
  title: string,
  showGraph: () => void,
  target: number,
  total: number,
  unit: string,
  compareTotal?: number
}

const TargetCard = ({
  title, target, total, unit, showGraph, compareTotal,
}: TargetCardProps) => {
  const totalInExcess: boolean = total > target;

  const MetricProgressBar = ({ value }) => {
    const inExcess: boolean = value > target;
    const lowerCutoff: number = styles.progressBarTrack.flex
      / (styles.progressBarTrack.flex + styles.progressBarTrackHigherCutoff.flex);
    const percentageOfLowerCutoff: number = Math
      .min(1, value / (target * lowerCutoff)) * 100;
    const percentageOfHigherCutoff: number = 100
      * (Math.max(0, (Math.min(1, value / target) - lowerCutoff))
        / (1 - lowerCutoff));
    const excessCutoff: number = styles.progressBarTrackHigherCutoff.flex
      / (styles.progressBarTrack.flex + styles.progressBarTrackHigherCutoff.flex);
    const excessPercentage: number = inExcess
      ? Math.min(1, (value - target) / (target * excessCutoff)) * 100 : 0;

    return <>
      <View style={styles.valueSection}>
        <View>
          <Text style={styles.label}>{'Current'}</Text>
          <View style={styles.countSection}>
            <Text style={styles.count}>
              {value >= 0 ? Math.round(value) : '-'}
              {'/'}
              {target}
            </Text>
            <Text style={styles.unitLabel}>{unit}</Text>
          </View>
        </View>
        {inExcess && <View style={styles.excessSection}>
          <Text style={styles.label}>{'Excess'}</Text>
          <View style={styles.countSection}>
            <Text style={[styles.count, { color: styles.statusLabelExcess.borderColor }]}>
              {Math.round(value - target)}
            </Text>
            <Text style={styles.unitLabel}>{unit}</Text>
          </View>
        </View>}
      </View>
      <View style={Style.row}>
        <View style={styles.progressBarTrack}>
          <View
            style={[
              styles.progressBar,
              inExcess && styles.progressBarExcess,
              { width: `${percentageOfLowerCutoff}%` },
            ]}
          />
        </View>
        <View style={[styles.progressBarTrack, styles.progressBarTrackHigherCutoff]}>
          <View
            style={[
              styles.progressBar,
              inExcess && styles.progressBarExcess,
              { width: `${percentageOfHigherCutoff}%` },
            ]}
          />
        </View>
        <View style={[
          styles.progressBarTrack,
          styles.progressBarTrackHigherCutoff,
          styles.progressBarTrackExcess,
        ]}>
          {inExcess && <View
            style={[styles.progressBar, styles.progressBarExcess, { width: `${excessPercentage}%` }]}
          />}
        </View>
      </View>
      <View style={[styles.capacitySection, {
        width: `${100 * ((styles.progressBarTrack.flex + styles.progressBarTrackHigherCutoff.flex)
          / (styles.progressBarTrack.flex + 2 * styles.progressBarTrackHigherCutoff.flex))}%`,
      }]}>
        <Text style={styles.label}>{'Target'}</Text>
        <View style={Style.row}>
          <Text style={styles.capacityValue}>
            {target ?? '-'}
          </Text>
          <Text style={[styles.unitLabel, styles.targetUnitLabel]}>{unit ?? '-'}</Text>
        </View>
      </View>
    </>;
  };

  return <View style={styles.container}>
    <View style={Style.row}>
      <Text style={styles.title}>{title}</Text>
      <PressableStyled
        onPress={showGraph}
        hitSlop={{
          top: 8, bottom: 8, left: 12, right: 12,
        }}
      >
        <FontAwesome
          name='angle-right'
          color={Colors.light.hereworksBlue500}
          size={24}
        />
      </PressableStyled>
    </View>
    {compareTotal ? <MetricProgressBar value={compareTotal} />
      : <View style={[styles.statusLabel, totalInExcess && styles.statusLabelExcess]}>
        <Text style={[
          styles.label,
          { color: (totalInExcess ? styles.statusLabelExcess : styles.statusLabel).borderColor },
        ]}>
          {`${totalInExcess ? 'Exceeding' : 'Meeting'} targets`}
        </Text>
      </View>}
    <MetricProgressBar value={total} />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: '#FFF',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  statusLabel: {
    marginTop: 4,
    marginBottom: 24,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    borderColor: Colors.light.hereworksGreen500,
    alignSelf: 'flex-start',
    backgroundColor: Colors.light.hereworksGreen50,
  },
  statusLabelExcess: {
    backgroundColor: Colors.light.hereworksRed50,
    borderColor: Colors.light.hereworksRed500,
  },
  label: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  countSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 16,
  },
  count: {
    fontSize: 30,
    lineHeight: 33,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  unitLabel: {
    fontSize: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginStart: 4,
  },
  targetUnitLabel: {
    fontSize: 11,
    alignSelf: 'flex-end',
    marginBottom: 4,
    marginStart: 2,
  },
  progressBarTrack: {
    flex: 0.54,
    height: 16,
    borderRadius: 100,
    backgroundColor: Colors.light.hereworksBlue50,
    overflow: 'hidden',
  },
  progressBar: {
    borderRadius: 100,
    height: '100%',
    backgroundColor: Colors.light.hereworksBlue500,
  },
  progressBarTrackHigherCutoff: {
    marginStart: 8,
    flex: 0.23,
  },
  progressBarTrackExcess: {
    backgroundColor: Colors.light.hereworksRed50,
  },
  progressBarExcess: {
    backgroundColor: Colors.light.hereworksRed500,
  },
  capacityValue: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksDarkBlue,
  },
  capacitySection: {
    ...Style.row,
    marginTop: 4,
    paddingEnd: 4,
  },
  valueSection: {
    flexDirection: 'row',
    marginTop: 12,
  },
  excessSection: {
    marginStart: 24,
  },
});

export default TargetCard;
